import React, { useState, useEffect } from 'react';
import { RefreshCw, Search, X, Eye, Printer, Trash2, Edit, Download, AlertTriangle } from 'lucide-react';
import ReactDOM from 'react-dom';
import MainService from '../../services/MainService';
import { getApiBaseUrl } from '../../services/appSettings';
import * as XLSX from 'xlsx';
import { useLanguage } from '../../contexts/LanguageContext';
import { getPageHeading } from '../../locales/navigation';

const ClientReturns = () => {
  const { language } = useLanguage();
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [voucherNo, setVoucherNo] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [viewReturn, setViewReturn] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const pageSize = 20;

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const loadReturns = async (page = 1) => {
    try {
      setLoading(true);
      setError('');

      const skip = (page - 1) * pageSize;
      const requestData = {
        Skip: skip,
        Take: pageSize,
        Keyword: searchKeyword || '',
        VoucherNo: voucherNo || '',
      };

      if (statusFilter) {
        requestData.Type = statusFilter;
      }

      const response = await MainService.ClientReturnsList(requestData);

      if (response?.Data) {
        setReturns(response.Data);
        setTotalCount(response.Total || 0);
        setCurrentPage(page);
      } else {
        setReturns([]);
        setTotalCount(0);
      }
    } catch (err) {
      console.error('Error loading client returns:', err);
      setError('Failed to load client returns');
      setReturns([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReturns(1);
  }, []);

  const handleSearch = () => {
    loadReturns(1);
  };

  const handleRefresh = () => {
    setSearchKeyword('');
    setVoucherNo('');
    setStatusFilter('');
    loadReturns(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= Math.ceil(totalCount / pageSize)) {
      loadReturns(newPage);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return dateString;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'NEW':
        return 'bg-blue-100 text-blue-800';
      case 'APPROVED':
        return 'bg-green-100 text-green-800';
      case 'STATUS DONE':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  const handleExportExcel = () => {
    try {
      const exportData = returns.map((item, index) => ({
        'S.No': (currentPage - 1) * pageSize + index + 1,
        'Ref No': item.RefNo || '',
        Date: formatDate(item.Date),
        Clients: item.ClientsNames?.length ? item.ClientsNames.join(', ') : '',
        Employee: item.Employee?.Name || '',
        'Created By': item.Createdby?.UserName || '',
        'Total Orders': item.TotalOrders || 0,
        Status: item.Status || '',
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Client Returns');

      const fileName = `ClientReturns_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      setSuccess('Excel file exported successfully');
    } catch (err) {
      console.error('Error exporting to Excel:', err);
      setError('Failed to export Excel file');
    }
  };

  const handlePrint = async (returnId) => {
    try {
      const response = await MainService.PrintClientReturn(returnId);
      if (response) {
        window.open(`${getApiBaseUrl()}ReportFiles/${response}`, '_blank');
      }
    } catch (err) {
      console.error('Error printing return:', err);
      setError('Failed to print return');
    }
  };

  const handleView = async (returnId) => {
    try {
      setViewLoading(true);
      const response = await MainService.GetClientReturnById(returnId);
      if (response) {
        setViewReturn(response);
      }
    } catch (err) {
      console.error('Error loading return:', err);
      setError('Failed to load return details');
    } finally {
      setViewLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      setLoading(true);
      const response = await MainService.DeleteClientReturn(deleteTarget.Id);
      if (response === 'Success') {
        setSuccess('Return deleted successfully');
        setDeleteTarget(null);
        loadReturns(currentPage);
      } else {
        setError(response || 'Failed to delete return');
      }
    } catch (err) {
      console.error('Error deleting return:', err);
      setError('Failed to delete return');
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="space-y-6">
      {error && (
        <div className="fixed top-24 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-[100] min-w-[300px]">
          <X className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="fixed top-24 right-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-[100] min-w-[300px]">
          <span className="flex-1">{success}</span>
          <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{getPageHeading(language, 'clientReturns')}</h1>
            <p className="text-sm text-gray-500 mt-1">View and manage all client returns</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleExportExcel}
              disabled={loading || returns.length === 0}
              className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Export to Excel"
            >
              <Download className="w-5 h-5" />
            </button>
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Refresh"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-3">
            <label className="block text-sm font-medium text-gray-700 mb-2">Search Keyword</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Search by Ref No, Client Name, Mobile..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
              {searchKeyword && (
                <button
                  onClick={() => {
                    setSearchKeyword('');
                    handleSearch();
                  }}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
          <div className="col-span-3">
            <label className="block text-sm font-medium text-gray-700 mb-2">Voucher No</label>
            <input
              type="text"
              value={voucherNo}
              onChange={(e) => setVoucherNo(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Search by Voucher No..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          <div className="col-span-3">
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setTimeout(() => handleSearch(), 100);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="">All</option>
              <option value="NEW">NEW</option>
              <option value="STATUS DONE">STATUS DONE</option>
            </select>
          </div>
          <div className="col-span-2 flex items-end">
            <button
              onClick={handleSearch}
              disabled={loading}
              className="w-full px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <Search className="w-4 h-4" />
              Search
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
          <table className="w-full">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">S.No</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ref No</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Clients</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created By</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total Orders</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Options</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading && returns.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-6 py-12 text-center">
                    <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2 text-gray-400" />
                    <p className="text-gray-500">Loading returns...</p>
                  </td>
                </tr>
              ) : returns.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-6 py-12 text-center text-gray-500">
                    <p className="text-lg mb-2">No returns found</p>
                  </td>
                </tr>
              ) : (
                returns.map((item, index) => (
                  <tr key={item.Id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {(currentPage - 1) * pageSize + index + 1}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.RefNo || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{formatDate(item.Date)}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {item.ClientsNames?.length ? (
                        <div className="max-w-xs">
                          <p className="truncate" title={item.ClientsNames.join(', ')}>
                            {item.ClientsNames.join(', ')}
                          </p>
                        </div>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{item.Employee?.Name || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{item.Createdby?.UserName || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 text-right">{item.TotalOrders || 0}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(item.Status)}`}>
                        {item.Status || '-'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => {
                            console.log('Edit return:', item.Id);
                          }}
                          className="p-1.5 text-yellow-600 hover:text-yellow-800 hover:bg-yellow-50 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleView(item.Id)}
                          disabled={viewLoading}
                          className="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
                          title="View"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handlePrint(item.Id)}
                          className="p-1.5 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-lg transition-colors"
                          title="Print"
                        >
                          <Printer className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDeleteTarget(item)}
                          className="p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-700">
            {totalCount > 0 ? (
              <>
                Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} returns
              </>
            ) : (
              'No returns found'
            )}
          </div>
          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || loading}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      disabled={loading}
                      className={`px-3 py-2 border rounded-lg text-sm transition-colors ${
                        currentPage === pageNum
                          ? 'bg-primary text-white border-primary'
                          : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages || loading}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>

      {deleteTarget && ReactDOM.createPortal(
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999]">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-amber-500" />
              <p className="text-gray-700">
                Delete return <strong>{deleteTarget.RefNo}</strong>? This cannot be undone.
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={loading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {viewReturn && ReactDOM.createPortal(
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999]">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-gray-900">Return — {viewReturn.RefNo}</h3>
              <button onClick={() => setViewReturn(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 text-sm">
              <div>
                <p className="text-gray-500">Date</p>
                <p className="font-medium text-gray-900">{formatDate(viewReturn.Date)}</p>
              </div>
              <div>
                <p className="text-gray-500">Status</p>
                <p className="font-medium text-gray-900">{viewReturn.Status || '-'}</p>
              </div>
              <div>
                <p className="text-gray-500">Employee</p>
                <p className="font-medium text-gray-900">{viewReturn.Employee?.Name || '-'}</p>
              </div>
              <div>
                <p className="text-gray-500">Total Orders</p>
                <p className="font-medium text-gray-900">{viewReturn.TotalOrders || 0}</p>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full border border-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs border border-gray-300">#</th>
                    <th className="px-4 py-2 text-left text-xs border border-gray-300">Ref No</th>
                    <th className="px-4 py-2 text-left text-xs border border-gray-300">Client</th>
                    <th className="px-4 py-2 text-left text-xs border border-gray-300">Client No</th>
                    <th className="px-4 py-2 text-left text-xs border border-gray-300">Mobile</th>
                    <th className="px-4 py-2 text-right text-xs border border-gray-300">Orders</th>
                    <th className="px-4 py-2 text-left text-xs border border-gray-300">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(viewReturn.ClientReturnClients || []).map((crc, index) => (
                    <tr key={crc.Id || index}>
                      <td className="px-4 py-2 border border-gray-300">{index + 1}</td>
                      <td className="px-4 py-2 border border-gray-300">{crc.RefNo}</td>
                      <td className="px-4 py-2 border border-gray-300">{crc.Client?.Name}</td>
                      <td className="px-4 py-2 border border-gray-300">{crc.Client?.ClientNo}</td>
                      <td className="px-4 py-2 border border-gray-300">{crc.Client?.MobileNo1}</td>
                      <td className="px-4 py-2 border border-gray-300 text-right">{crc.TotalOrders}</td>
                      <td className="px-4 py-2 border border-gray-300">{crc.Status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => handlePrint(viewReturn.Id)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
              >
                <Printer className="w-4 h-4" /> Print
              </button>
              <button
                onClick={() => setViewReturn(null)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ClientReturns;
